import styled from "@emotion/styled"
import { useDictionary } from "contexts/Dictionary"
import { a11yOutline } from "css/a11y"
import getMediaQuery from "css/breakpoints"
import { motion, useMotionValue, useMotionValueEvent } from "framer-motion"
import { useState } from "react"
import { useValue } from "./context"

const Root = styled(motion.nav)`
  align-items: center;
  display: flex;
  height: 100vh;
  grid-column: doc;
  grid-row: 1/-1;
  justify-content: end;
  margin-inline-end: 3%;
  pointer-events: none;
  position: sticky;
  top: 0;
  transition: opacity 0.2s linear;
  z-index: 25;

  ${getMediaQuery("m")} {
    margin-inline-end: 4%;
  }
`

const PageContainer = styled.ul`
  align-items: center;
  border: none;
  border-radius: 10px;
  display: grid;
  grid-auto-flow: row;
  grid-gap: 0.75rem;
  height: min-content;
  margin: 0;
  padding: 0;
  top: 0;

  &:has(.focus-visible) {
    ${a11yOutline}
    outline-offset: 0.5rem !important;
  }
  html.prefers-contrast & {
    background: #fff;
    padding: 1.25rem 0.625rem;
  }
`

const PageGroup = styled.li`
  display: flex;
`

const Label = styled(motion.a)`
  background: var(--bgcolor);
  background: rgb(from var(--bgcolor, #d4d4d4) r g b / 0.6);
  border: none;
  border-radius: 5px;
  cursor: default;
  font-size: 0;
  padding: 0;
  pointer-events: all;
  position: relative;
  transition: background-color 0.3s ease-out;
  width: 0.1875rem;

  @media (hover: hover) and (pointer: fine) {
    &.unchecked:hover {
      cursor: pointer;
    }
  }

  &.unchecked::after {
    background: transparent;
    content: "";
    display: block;
    height: 1.875rem;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 1.25rem;
    z-index: 0;
  }

  ${getMediaQuery("m")} {
    &.unchecked::after {
      transform: translate(-50%, -50%);
      width: 1.875rem;
    }
  }
  html.prefers-contrast & {
    background: black;
  }
`

const variants = {
  checked: { height: "3.125rem" },
  unchecked: { height: "1.25rem" },
}

function Page({ index, label, onPageClick }) {
  const { currentIndex } = useValue()

  const [checked, setChecked] = useState(currentIndex.get() === index)

  function onSlideChange(i) {
    setChecked(index === i)
  }
  useMotionValueEvent(currentIndex, "change", onSlideChange)

  const handleClick = e => {
    e.preventDefault()
    onPageClick(index)
  }

  return (
    <PageGroup>
      <Label
        href={`#testimonee--${index}`}
        aria-current={checked}
        onClick={handleClick}
        variants={variants}
        initial={checked ? "checked" : "unchecked"}
        animate={checked ? "checked" : "unchecked"}
        className={checked ? "checked" : "unchecked"}
        transition={{ type: "tween", ease: "easeOut", duration: 0.2 }}
      >
        {label || `slide ${index + 1}`}
      </Label>
    </PageGroup>
  )
}

export default function Pagination({ sections }) {
  const { targetSlide, currentIndex } = useValue()
  const bgcolor = useMotionValue("#d4d4d4")
  const dictionary = useDictionary()

  const onPageClick = index => {
    targetSlide.set(index)
  }

  function onCurrentChange(theme) {
    if (theme === "dark-theme") {
      bgcolor.set("#d4d4d4")
    } else {
      bgcolor.set("#212121")
    }
  }

  useMotionValueEvent(currentIndex, "change", index => {
    const theme = sections[index]?.props?.theme || "dark-theme"
    onCurrentChange(theme)
  })

  return (
    <Root style={{ "--bgcolor": bgcolor }} aria-label={dictionary.navigateThePage()}>
      <PageContainer aria-label={dictionary.navigateThePage()}>
        {sections.map(({ key }, i) => (
          <Page key={`testimonee-page-${key}`} index={i} label='' onPageClick={onPageClick} />
        ))}
      </PageContainer>
    </Root>
  )
}
